import { isDesktop } from './desktop.ts'

/**
 * Registers the shell service worker.
 *
 * Production only. In development Vite serves unbundled modules and there is no
 * worker to register — offline is tested against a real build (`pnpm preview`),
 * which is also the only honest way to test it.
 *
 * Failure here is deliberately non-fatal. A browser that refuses the worker —
 * private window, unsupported context, insecure origin — still gets a fully
 * working online application, and the connectivity indicator still tells the
 * truth. Offline is a capability, not a prerequisite.
 */
export const registerServiceWorker = (): void => {
  if (!import.meta.env.PROD) return
  if (!('serviceWorker' in navigator)) return

  /*
   * No desktop, nenhum service worker (F212).
   *
   * Lá quem serve a tela é o próprio shell, da máquina, e o offline é do
   * Edge: um worker só acrescentava uma segunda cópia da tela e da
   * configuração, que sobrevivia a uma atualização do programa. Um que já
   * esteja registrado de uma versão anterior é removido, com o que guardou.
   */
  if (isDesktop()) {
    void forgetServiceWorker()
    return
  }

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Intentionally silent: see the note above. Reporting this to the
      // operator would be reporting something they cannot act on.
    })
  })
}

/** Tira o worker desta origem e apaga o que ele guardou. */
export const forgetServiceWorker = async (): Promise<void> => {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registrations.map(async (registration) => await registration.unregister())
    )
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(
        keys
          .filter((key) => key.startsWith('aeris-shell-'))
          .map(async (key) => await caches.delete(key))
      )
    }
  } catch {
    // Sem worker para tirar, ou sem permissão: não há o que avisar.
  }
}
